import type { RootState } from './../redux/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { IMenuCounters, IUser } from '../models/IUser/IUser';
import { API_BASE_URL } from '../helpers/common';
import { MenuCount } from '../Components/Common/Menu/useLinks/useLinks';

export interface IStockOwnerAccount {
  id: number;
  code: string;
  name: string;
  userId: number;
  companyId: number;
  company?: {
    id: number;
    name: string;
  };
  city?: {
    id: number;
    name: string;
  };
  isActive: boolean;
}

export interface IGetUserStockOwnersResponse {
  status: string;
  data: IStockOwnerAccount[];
  meta: {
    userId: number;
    count: number;
  };
}

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      token && headers.set('Authorization', `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (build) => ({
    createCustomerUser: build.mutation<any, any>({
      transformResponse: (response: { data: any }) => response.data,
      query: (data) => ({
        url: '/customers/user/create',
        method: 'POST',
        body: data,
      }),
    }),
    getUser: build.query<IUser, string>({
      transformResponse: (response: { data: IUser }) => response.data,
      query: () => ({
        url: '/users/profile',
        method: 'GET',
      }),
    }),
    getMenuCounters: build.query<IMenuCounters, string>({
      transformResponse: (response: { data: IMenuCounters }) => response.data,
      query: () => ({
        url: '/users/profile/counter',
        method: 'GET',
      }),
    }),
    updateProfile: build.mutation<IUser, {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      avatar?: string;
    }>({
      transformResponse: (response: { data: IUser }) => response.data,
      query: (data) => ({
        url: '/users/profile',
        method: 'PATCH',
        body: data,
      }),
    }),
    updateProfileFormData: build.mutation<IUser, FormData>({
      transformResponse: (response: { data: IUser }) => response.data,
      query: (formData) => ({
        url: '/users/profile',
        method: 'PATCH',
        body: formData,
      }),
    }),
    getUserStockOwners: build.query<IStockOwnerAccount[], void>({
      transformResponse: (response: IGetUserStockOwnersResponse) => response.data,
      query: () => ({
        url: '/users/me/stock-owners',
        method: 'GET',
      }),
    }),
    getUsersList: build.query<Array<{ id: number; name: string; email: string }>, void>({
      transformResponse: (response: { data: any[] }) => 
        response.data.map((user: any) => ({
          id: user.id,
          name: user.userName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
          email: user.email,
        })),
      query: () => ({
        url: '/users/list',
        method: 'GET',
      }),
    }),
    getUsersWithCreateOrderAccess: build.query<Array<{ id: number; name: string; email: string }>, void>({
      transformResponse: (response: any) => {
        if (Array.isArray(response)) return response;
        if (response?.data && Array.isArray(response.data)) return response.data;
        return [];
      },
      query: () => ({
        url: '/users/access/isCreateOrder',
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useCreateCustomerUserMutation,
  useGetUserQuery,
  useGetMenuCountersQuery,
  useUpdateProfileMutation,
  useGetUserStockOwnersQuery,
  useGetUsersListQuery,
  useGetUsersWithCreateOrderAccessQuery,
} = userApi;
